import React, { useContext, useEffect, useRef, useState } from "react";
import CommonModal from "../../Modals/CommonModal";
import Input from "../Input";
import CustomTooltip from "../Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboard,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import Button from "../Button";
import { Tab, Tabs } from "react-bootstrap";
import LaserLINKSettingsTab from "./LaserLINKSettingsTab";
import AccessGateOptionsTab from "./AccessGateOptionsTab";
import PopupOptionsTab from "./PopupOptionsTab";
import SendNotificationTab from "./SendNotificationTab";
import UserInfoContext from "../../../utis/userInfoContext";
import apiGETRequest from "../../../utis/apiRequest";
import { networkService } from "../../config/axios";
import {
  defaultLaserLINKData,
  defaultPopupOptions,
} from "../../../hooks/utils";
import styles from "./links.module.css";

const createEmbedIframeCode = (link) => {
  return `<div style="padding:56.25% 0 0 0;position:relative;"><iframe src="${link}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write" style="position:absolute;top:0;left:0;width:100%;height:100%;background:transparent;" title="Sales Intro"></iframe></div>`;
};
const LaserLinkModal = ({
  laserItem = null,
  time_zone_list,
  docData,
  triggerCancelProcess = () => {},
  batchStatus = null,
  updateSelectedKDFLink = () => {},
  triggerUpdateList = () => {},
  currentUser,
  language,
  thumbnail,
}) => {
  const { userInfoData, setShowNotifications } = useContext(UserInfoContext);
  const trackingLinkRef = useRef(null);
  const domainsCacheRef = useRef(null);
  const profileCacheRef = useRef(null);
  const copyAnimationTimeoutRef = useRef(null);

  const [editLaserLinkItem, setEditLaserLinkItem] = useState(null);
  const [getDocFiles, setDocFiles] = useState([]);
  const [getDomains, setDomains] = useState({});
  const [getProfile, setProfile] = useState({});
  const [popupOptions, setPopupOptions] = useState({ enter_pop: {} });
  const [getDefault_link_texts, setDefault_link_texts] = useState(
    window?.default_link_texts
  );
  const [embedCode, setEmbedCode] = useState(null);
  const [getValidationStatus, setValidationStatus] = useState(false);
  const [getValiationTabMessage, setValiationTabMessage] = useState({});
  const [saveButtonStatus, setSaveButtonStatus] = useState(null);
  const [errorList, setErrorList] = useState({
    settings: [],
    access: [],
    popup: [],
  });
  const [isError, setIsError] = useState(false);
  const [languageConfirm, setLanguageConfirm] = useState(null);
  const initialLaserLinkItem = useRef(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isCopyAnimating, setIsCopyAnimating] = useState(false);
  const isInitializingRef = useRef(false);
  const justClosedRef = useRef(false);

  const modalRootRef = useRef(null);
  const titleInputRef = useRef(null);

  const closeModal = () => {
    setEditLaserLinkItem(null);
    setSaveButtonStatus(null);
    setIsDirty(false);
    setValidationStatus(false);
    setErrorList({
      settings: [],
      access: [],
      popup: [],
    });
    initialLaserLinkItem.current = null;
    isInitializingRef.current = false;
    // Do NOT reset justClosedRef here: Copy Laserlink & Close sets it to true
    // and relies on it to prevent the laserItem useEffect from reopening the modal.
    // Only the Copy & Close path resets it (in its setTimeout).
    triggerCancelProcess(null);
  };

// useEffect(() => {
//   if (!editLaserLinkItem) return;

//   const root = modalRootRef.current;
//   const target = root ?? document;
//   let last = 0;
//   const showAlert = (e) => {
//     const now = Date.now();
//     if (now - last < 800) return; // throttle
//     last = now;
//     // alert(`User interaction detected (${e.type}) 🚀`);
//   };

//   // cover almost all UI interactions
//   const events = [
//     "input",        // typing, range slider, etc.
//     "change",       // checkbox, select, radio, input blur-commit
//     "click",        // clicks
//     "keydown",      // key presses (ENTER on form, space on checkbox)
//     "pointerdown",  // mouse/touch/pen press
//     "touchstart",   // explicit touch (older browsers)
//     "submit"        // form submit
//   ];
//   events.forEach(evt => target.addEventListener(evt, showAlert, true));

//   return () => {
//     events.forEach(evt => target.removeEventListener(evt, showAlert, true));
//   };
// }, [editLaserLinkItem]); // modal open/close par re-attach

useEffect(() => {
  if (!editLaserLinkItem) return;

  const root = modalRootRef.current;
  const target = root ?? document;

  const handleUserInteraction = () => {
    // Instead of alert, flip the button back to "Save changes"
    setSaveButtonStatus(false);
  };

  const events = ["input", "change", "keydown", "submit"];
  events.forEach(evt => target.addEventListener(evt, handleUserInteraction, true));

  return () => {
    events.forEach(evt => target.removeEventListener(evt, handleUserInteraction, true));
  };
  }, [editLaserLinkItem]);

  useEffect(() => {
    if (!editLaserLinkItem || batchStatus) return;

    const timer = setTimeout(() => {
      // Only focus title if user is NOT currently typing in another input/editor
      const activeElement = document.activeElement;
      const isUserTyping = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable ||
        activeElement.closest('.tox-edit-area') // TinyMCE editor
      );

      // Don't steal focus if user is typing somewhere else
      if (!isUserTyping) {
        titleInputRef.current?.focus();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [editLaserLinkItem, batchStatus]);

  useEffect(() => {
    // During initialization, ignore changes to prevent flicker
    if (isInitializingRef.current) {
      return;
    }
    
    if (editLaserLinkItem && initialLaserLinkItem.current) {
      const hasChanges =
        JSON.stringify(editLaserLinkItem) !==
        JSON.stringify(initialLaserLinkItem.current);
      setIsDirty(hasChanges);
    } else if (editLaserLinkItem && !initialLaserLinkItem.current) {
      // New item mode, treat as dirty until first save
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  }, [editLaserLinkItem]);

  const updateDomainListEvent = async () => {
    if (domainsCacheRef.current) {
      setDomains(domainsCacheRef.current);
      return domainsCacheRef.current;
    }
    if (userInfoData?.id || currentUser?.id) {
      let domainListGET = await apiGETRequest(
        `admin/user_domains.json?user_id=${userInfoData?.id ?? currentUser?.id}`
      );
      domainsCacheRef.current = domainListGET;
      setDomains(domainListGET);
      return domainListGET;
    } else {
      return "Loading...";
    }
  };

  const updateProfileEvent = async () => {
    if (profileCacheRef.current) {
      setProfile(profileCacheRef.current);
      return profileCacheRef.current;
    }
    if (userInfoData?.id == undefined) {
      return "Loading...";
    }
    let userProfileGET = await apiGETRequest(
      `profiles/${userInfoData.id}.json`
    );
    profileCacheRef.current = userProfileGET;
    setProfile(userProfileGET);
    return userProfileGET;
  };

  useEffect(() => {
    if (getProfile) {
      profileCacheRef.current = getProfile;
    }
  }, [getProfile]);

  // Helper function to check if a page has Jump to Page elements
  const checkLandingPageHasJumpToPage = async (docId, landingPage) => {
    try {
      const response = await networkService(
        `docs/all-elements/${docId}.json`,
        "GET"
      );

      if (response?.data && typeof response.data === 'object') {
        const elements = Object.values(response.data);

        // Check if landing page has any Jump to Page elements
        const landingPageHasJump = elements.some(element => {
          if (!element) return false;

          const isOnLandingPage = (element.page || element.geometry?.page) === Number(landingPage);
          const hasJumpToPage = element.action === 'page' || element.value?.page_link || element.value?.lj_page_link;

          return isOnLandingPage && hasJumpToPage;
        });

        if (landingPageHasJump) {
          console.log('🎯 LaserLINK Modal - Auto-selecting CYOA:', {
            docId,
            landingPage,
            reason: 'Landing page has Jump to Page elements',
            action: 'Automatically enabling CYOA mode'
          });
        }

        return landingPageHasJump;
      }

      return false;
    } catch (error) {
      console.warn('⚠️ Failed to check Jump to Page elements:', error);
      return false;
    }
  };

  const updateLanguageSelection = (value) => {
    setEditLaserLinkItem((prevState) => {
      if (!prevState) {
        return prevState;
      }

      const languages = prevState?.doc_files_languages || [];
      const match = languages.find((lang) => {
        const locale = lang?.language?.locale || lang?.language;
        return locale === value;
      });

      return {
        ...prevState,
        language: value,
        languages: value,
        language_flag: match?.language ?? prevState?.language_flag,
      };
    });
    setErrorList({ settings: [], access: [], popup: [] });
    setSaveButtonStatus(false);
  };

    const handleOnchangeEvent = (e) => {
    if (!e?.target) {
      return;
    }

    const { name, value } = e.target;

    if (name === "language") {
      const currentLanguage =
        editLaserLinkItem?.language ?? editLaserLinkItem?.languages;

      if (!value || value === currentLanguage) {
        return;
      }

      if (editLaserLinkItem?.has_stats) {
        setLanguageConfirm(value);
        return;
      }

      updateLanguageSelection(value);
      return;
    }

    setEditLaserLinkItem((prevState) => ({
      ...prevState,
      [name]: value,
    }));

        setErrorList({ settings: [], access: [], popup: [] });
        setSaveButtonStatus(false);
    };

  const updateUserLightboxesDoc = async (item) => {
    let fetchUserLightBoxData = await apiGETRequest(
      `docs/all-user-lightboxes/doc.json?by_trigger=yes&doc_id=${
        item?.doc != undefined ? item?.doc?.id : item?.id
      }&language=${
        language ?? item?.doc != undefined
          ? item?.language
          : item?.doc_files_languages[0].language.locale
      }`
    );
    if (fetchUserLightBoxData?.error == undefined) {
      setPopupOptions((prevState) => ({
        ...prevState,
        enter_pop: fetchUserLightBoxData?.doc,
      }));
    }
  };

  const updateUserLightboxesPage = async (item) => {
    let fetchUserLightBoxPage = await apiGETRequest(
      `docs/all-user-lightboxes/page.json?by_trigger=yes&doc_id=${
        item?.doc != undefined ? item?.doc?.id : item?.id
      }&language=${
        language ?? item?.doc != undefined
          ? item?.language
          : item?.doc_files_languages[0].language.locale
      }`
    );
    let flashPopData = {};
    if (Object.keys(fetchUserLightBoxPage)?.length > 0) {
      if (fetchUserLightBoxPage?.error == undefined) {
        // dummyPopTimedData
        flashPopData = Object.keys(fetchUserLightBoxPage?.page)
          ?.map((item) => fetchUserLightBoxPage?.page[item])
          ?.reduce((acc, value) => {
            return { ...acc, ...value };
          }, {});
      }
    }
    setPopupOptions((prevState) => ({ ...prevState, timed_pop: flashPopData }));
  };

  const updateUserExitPopData = async (item) => {
    let fetchOnExitPopData = await apiGETRequest(
      `docs/all-user-lightboxes/onexit.json?by_trigger=yes&doc_id=${
        item?.doc != undefined ? item?.doc?.id : item?.id
      }&language=${
        language ?? item?.doc != undefined
          ? item?.language
          : item?.doc_files_languages[0].language.locale
      }`
    );
    let exitPopData = {};
    if (Object.keys(fetchOnExitPopData)?.length > 0) {
      if (fetchOnExitPopData?.error == undefined) {
        exitPopData = fetchOnExitPopData?.onexit;
      }
    }
    setPopupOptions((prevState) => ({ ...prevState, exit_pop: exitPopData }));
  };

  const updateLaserLinkData = async (itemId, itemStatus) => {
    if (itemId != undefined) {
      let fileDataGet = await apiGETRequest(
        `docs/${itemId}/files.json?active=${itemStatus}`
      );
      if (fileDataGet?.error == undefined) {
        setEditLaserLinkItem((prev) => {
          if (!prev) return prev;
          const updated = {
            ...prev,
            doc_files_languages: fileDataGet,
          };
          // Update initialLaserLinkItem.current to keep isDirty check accurate
          if (initialLaserLinkItem.current) {
            initialLaserLinkItem.current = JSON.parse(JSON.stringify(updated));
          }
          return updated;
        });
        setDocFiles(fileDataGet);
      }
    }
  };


  const handleLaserLINKSettingsModal = async (e) => {
    // Set initialization flag to prevent isDirty from being set during initialization
    isInitializingRef.current = true;

    updateDomainListEvent();
    updateProfileEvent();
    updateUserLightboxesDoc(e);
    updateUserLightboxesPage(e);
    updateUserExitPopData(e);

    setLanguageConfirm(null);
    
    // Check if we have complete checkbox data from link list
    const hasCompleteCheckboxData = e?.show_toolbar !== undefined &&
                                     e?.disable_scroll !== undefined &&
                                     e?.disable_settings !== undefined;
    
    if (hasCompleteCheckboxData) {
      // Link list has complete data - fetch file data in parallel to avoid flicker
      const initialData = {
        ...e,
        doc_id: e?.doc?.id,
        expire_time: e?.doc?.expire_time ?? "0:0",
      };
      
      // Deduplicate notification_emails from initial data
      const initialEmails = initialData?.notification_emails || [];
      const normalizedInitialEmails = initialEmails
        .map((item) => {
          if (typeof item === "string") return { email: item };
          if (item?.email) return { email: item.email };
          return null;
        })
        .filter((item) => item?.email);
      
      // Remove duplicates based on email address (case-insensitive)
      const uniqueInitialEmails = [];
      const seenInitialEmails = new Set();
      normalizedInitialEmails.forEach((item) => {
        const emailLower = item.email.toLowerCase();
        if (!seenInitialEmails.has(emailLower)) {
          seenInitialEmails.add(emailLower);
          uniqueInitialEmails.push(item);
        }
      });
      
      try {
        const [fileDataGet, landingPageHasJump] = await Promise.all([
          apiGETRequest(`docs/${e?.doc?.id}/files.json?active=${e?.doc_file_status}`),
          checkLandingPageHasJumpToPage(e?.doc?.id, initialData.landing_page || 1)
        ]);

        let completeInitialData = {
          ...initialData,
          notification_emails: uniqueInitialEmails,
          doc_files_languages: fileDataGet?.error === undefined ? fileDataGet : initialData.doc_files_languages,
        };

        // Auto-enable CYOA if landing page has Jump to Page elements and the user
        // hasn't already explicitly configured a scroll-control mode.
        if (landingPageHasJump && !completeInitialData.cyoa_enabled && !completeInitialData.disable_settings?.disable_on) {
          completeInitialData = {
            ...completeInitialData,
            cyoa_enabled: true,
            disable_scroll: true,
            disable_settings: {
              ...completeInitialData.disable_settings,
              disable_on: 'cyoa'
            }
          };
        }

        setEditLaserLinkItem(completeInitialData);
        initialLaserLinkItem.current = JSON.parse(JSON.stringify(completeInitialData));
        setIsDirty(false);
        isInitializingRef.current = false;

        if (fileDataGet?.error === undefined) {
          setDocFiles(fileDataGet);
        }
      } catch (error) {
        setEditLaserLinkItem(initialData);
        initialLaserLinkItem.current = JSON.parse(JSON.stringify(initialData));
        setIsDirty(false);
        isInitializingRef.current = false;
      }
    } else {
      // Link list data is incomplete - fetch complete data in parallel, then set state once
      const basicData = {
        ...e,
        doc_id: e?.doc?.id,
        expire_time: e?.doc?.expire_time ?? "0:0",
      };

      try {
        const [linkDataResponse, fileDataGet, landingPageHasJump] = await Promise.all([
          networkService(`links/${e?.id}.json?for_tl=true`, "GET"),
          apiGETRequest(`docs/${e?.doc?.id}/files.json?active=${e?.doc_file_status}`),
          checkLandingPageHasJumpToPage(e?.doc?.id, basicData.landing_page || 1)
        ]);

        if (linkDataResponse.status === 200) {
          // Deduplicate notification_emails from server response
          const serverEmails = linkDataResponse.data?.notification_emails || [];
          const normalizedServerEmails = serverEmails
            .map((item) => {
              if (typeof item === "string") return { email: item };
              if (item?.email) return { email: item.email };
              return null;
            })
            .filter((item) => item?.email);

          // Remove duplicates based on email address (case-insensitive)
          const uniqueEmails = [];
          const seenEmails = new Set();
          normalizedServerEmails.forEach((item) => {
            const emailLower = item.email.toLowerCase();
            if (!seenEmails.has(emailLower)) {
              seenEmails.add(emailLower);
              uniqueEmails.push(item);
            }
          });

          let completeData = {
            ...basicData,
            ...linkDataResponse.data,
            expire_time: linkDataResponse.data?.expire_time ?? basicData.expire_time,
            notification_emails: uniqueEmails,
            languages: linkDataResponse.data?.language,
            doc_files_languages: fileDataGet?.error === undefined ? fileDataGet : basicData.doc_files_languages,
          };

          // Auto-enable CYOA if landing page has Jump to Page elements and the user
          // hasn't already explicitly configured a scroll-control mode.
          if (landingPageHasJump && !completeData.cyoa_enabled && !completeData.disable_settings?.disable_on) {
            completeData = {
              ...completeData,
              cyoa_enabled: true,
              disable_scroll: true,
              disable_settings: {
                ...completeData.disable_settings,
                disable_on: 'cyoa'
              }
            };
          }

          setEditLaserLinkItem(completeData);
          initialLaserLinkItem.current = JSON.parse(JSON.stringify(completeData));
          setIsDirty(false);
          isInitializingRef.current = false;

          if (fileDataGet?.error === undefined) {
            setDocFiles(fileDataGet);
          }
        } else {
          setEditLaserLinkItem(basicData);
          initialLaserLinkItem.current = JSON.parse(JSON.stringify(basicData));
          setIsDirty(false);
          isInitializingRef.current = false;
          updateLaserLinkData(e?.doc?.id, e?.doc_file_status);
        }
      } catch (error) {
        setEditLaserLinkItem(basicData);
        initialLaserLinkItem.current = JSON.parse(JSON.stringify(basicData));
        setIsDirty(false);
        isInitializingRef.current = false;
        updateLaserLinkData(e?.doc?.id, e?.doc_file_status);
      }
    }
  };

  // const handleSaveLaserLINKData = async () => {
  //   setIsSubmitting(true);
  //   try {
  //     if (!saveButtonStatus) {
  //       setValidationStatus(true);

  //       if (!batchStatus) {
  //         const isNameEmpty = editLaserLinkItem?.name?.trim() === "";
  //         const nameExists = errorList?.settings?.includes("name");

  //         setErrorList((prev) => ({
  //           ...prev,
  //           settings: isNameEmpty
  //             ? nameExists
  //               ? prev.settings
  //               : [...prev.settings, "name"]
  //             : nameExists
  //             ? prev.settings.filter((st) => st !== "name")
  //             : prev.settings,
  //         }));
  //       }
  //     } else {
  //       const text = editLaserLinkItem?.tracking_link ?? "";
  //       if (text) {
  //         if (navigator.clipboard?.writeText) {
  //           await navigator.clipboard.writeText(text);
  //         } else {
  //           const textarea = document.createElement("textarea");
  //           textarea.value = text;
  //           document.body.appendChild(textarea);
  //           textarea.select();
  //           document.execCommand("copy");
  //           document.body.removeChild(textarea);
  //         }
  //       }

  //       setEditLaserLinkItem(null);
  //       triggerCancelProcess(null);
  //       triggerUpdateList(true);
  //     }
  //   } catch (error) {
  //     console.warn("Clipboard or Save Error:", error);
  //   }
  // };
  const handleSaveLaserLINKData = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    // Block save completely when validation errors already exist (e.g. landing_page_gt_optin).
    // This prevents any intermediate / stuck state — the button should have been disabled,
    // but guard here as a safety net too.
    if (!saveButtonStatus && Object.values(errorList).some(arr => arr.length > 0)) {
      return;
    }

    // Copy & Close path: skip validation/submission entirely — link is already saved.
    if (saveButtonStatus === true) {
      const text = editLaserLinkItem?.tracking_link ?? "";
      if (text) {
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
          } else {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
          }
          setShowNotifications({
            showToast: true,
            toastType: "success",
            toastMessage: "LaserLINK copied",
            delay: 2000,
            autohide: true,
          });
        } catch (error) {
          setShowNotifications({
            showToast: true,
            toastType: "danger",
            toastMessage: "Unable to copy LaserLINK",
          });
        }
      }
      justClosedRef.current = true;
      updateSelectedKDFLink(null);
      closeModal();
      setTimeout(() => {
        triggerUpdateList(true);
        setTimeout(() => {
          justClosedRef.current = false;
        }, 1000);
      }, 500);
      return;
    }

    setIsSubmitting(true);

  // 1) trigger child-tab validations
  setValidationStatus(true);

  // 2) Title / Batch file errors add/update (settings tab)
  if (!batchStatus) {
    const isNameEmpty = !editLaserLinkItem?.name?.trim();
    setErrorList(prev => ({
      ...prev,
      settings: isNameEmpty
        ? Array.from(new Set([...(prev.settings || []), "name"]))
        : (prev.settings || []).filter(k => k !== "name"),
    }));
  } else {
    const noBatch =
      !editLaserLinkItem?.batch_link_array?.length && !batchFile;
    setErrorList(prev => ({
      ...prev,
      settings: noBatch
        ? Array.from(new Set([...(prev.settings || []), "batch_file"]))
        : (prev.settings || []).filter(k => k !== "batch_file"),
    }));
  }

  try {
    if (!saveButtonStatus) {
      // actual submit attempt will run from the errorList effect
      // (do nothing here)
    }
  } catch (error) {
    // Error handling
  }
};

  const [batchFile, setBatchFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitLaserLINKDataEvent = async () => {
    setValidationStatus(false);
    if (
      Object.keys(errorList)?.filter((errKey) => errorList[errKey]?.length > 0)
        ?.length == 0 &&
      getValidationStatus == true
    ) {
      let editLaserLinkItemCopy = JSON.parse(JSON.stringify(editLaserLinkItem));
      if (editLaserLinkItem?.name == "" && batchStatus != true) {
        setValiationTabMessage((prev) => ({
          ...prev,
          settings: { error: true },
        }));
        return "";
      }

      if (editLaserLinkItem.optin_2nd_page_viewed) {
        editLaserLinkItem.opt_in.show_delay = 1;
        editLaserLinkItem.opt_in.show_on_page = "2";
      }

      // Deduplicate notification_emails before sending to server
      const emailsToSend = editLaserLinkItem?.notification_emails || [];
      const normalizedEmailsToSend = emailsToSend
        .map((item) => {
          if (typeof item === "string") return { email: item };
          if (item?.email) return { email: item.email };
          return null;
        })
        .filter((item) => item?.email);
      
      // Remove duplicates based on email address (case-insensitive)
      const uniqueEmailsToSend = [];
      const seenEmailsToSend = new Set();
      normalizedEmailsToSend.forEach((item) => {
        const emailLower = item.email.toLowerCase();
        if (!seenEmailsToSend.has(emailLower)) {
          seenEmailsToSend.add(emailLower);
          uniqueEmailsToSend.push(item);
        }
      });

      let optionsPayload = {
        ...defaultPopupOptions,
        ...editLaserLinkItem.opt_in,
      };
      let laserLinkPayload =
        batchStatus == true
          ? {
              ...editLaserLinkItem,
              notification_emails: uniqueEmailsToSend,
              optin_options: { ...optionsPayload },
            }
          : {
              ...editLaserLinkItem,
              notification_emails: uniqueEmailsToSend,
            };
      let laserLINKURL =
        batchStatus == true
          ? "create_batch_links.json"
          : editLaserLinkItem?.id != undefined
          ? `links/${editLaserLinkItem?.id}.json`
          : "links.json";
      let submitLaserLINKData = await networkService(
        laserLINKURL,
        editLaserLinkItem?.id != undefined ? "PATCH" : "POST",
        laserLinkPayload
      );
      let linkOpitonsURL =
        editLaserLinkItem?.id != undefined
          ? `link_options/${submitLaserLINKData?.data?.tracking_link_option?.id}.json`
          : "link_options.json";
      if (
        submitLaserLINKData.status == 201 ||
        submitLaserLINKData.status == 200
      ) {
        if (batchStatus != true) {
          let submitLaserLINKOpitionsData = await networkService(
            linkOpitonsURL,
            editLaserLinkItem?.id != undefined ? "PATCH" : "POST",
            { ...optionsPayload, tracking_link_id: submitLaserLINKData.data.id }
          );
          // Don't update editLaserLinkItem if we're about to close (Copy & Close scenario)
          if (!justClosedRef.current) {
            // Deduplicate notification_emails to prevent duplicates
            const serverEmails = submitLaserLINKData.data?.notification_emails || [];
            const normalizedServerEmails = serverEmails
              .map((item) => {
                if (typeof item === "string") return { email: item };
                if (item?.email) return { email: item.email };
                return null;
              })
              .filter((item) => item?.email);
            
            // Remove duplicates based on email address (case-insensitive)
            const uniqueEmails = [];
            const seenEmails = new Set();
            normalizedServerEmails.forEach((item) => {
              const emailLower = item.email.toLowerCase();
              if (!seenEmails.has(emailLower)) {
                seenEmails.add(emailLower);
                uniqueEmails.push(item);
              }
            });
            
            const updatedData = {
              ...editLaserLinkItem,
              ...submitLaserLINKData.data,
              notification_emails: uniqueEmails,
              opt_in: { ...submitLaserLINKOpitionsData.data },
              thumb: submitLaserLINKData?.data?.thumbnail,
              expire_time:
                submitLaserLINKData?.data?.expire_time ?? "0:0",
            };
            
            setEditLaserLinkItem(updatedData);
            initialLaserLinkItem.current = JSON.parse(JSON.stringify(updatedData));
          }
        } else {
          // Don't update editLaserLinkItem if we're about to close (Copy & Close scenario)
          if (!justClosedRef.current) {
            // Deduplicate notification_emails to prevent duplicates
            const serverEmails = submitLaserLINKData.data?.notification_emails || [];
            const normalizedServerEmails = serverEmails
              .map((item) => {
                if (typeof item === "string") return { email: item };
                if (item?.email) return { email: item.email };
                return null;
              })
              .filter((item) => item?.email);
            
            // Remove duplicates based on email address (case-insensitive)
            const uniqueEmails = [];
            const seenEmails = new Set();
            normalizedServerEmails.forEach((item) => {
              const emailLower = item.email.toLowerCase();
              if (!seenEmails.has(emailLower)) {
                seenEmails.add(emailLower);
                uniqueEmails.push(item);
              }
            });
            
            const updatedData = {
              ...editLaserLinkItem,
              ...submitLaserLINKData.data,
              notification_emails: uniqueEmails,
              thumb: submitLaserLINKData?.data?.thumbnail,
              expire_time:
                submitLaserLINKData?.data?.expire_time ?? "0:0",
            };
            
            setEditLaserLinkItem(updatedData);
            initialLaserLinkItem.current = JSON.parse(JSON.stringify(updatedData));
          }
        }
        setValidationStatus(false);
        if (batchStatus == true) {
          setBatchFile(null);
          setEditLaserLinkItem(null);

          triggerCancelProcess(null);
          triggerUpdateList(true);
          setShowNotifications({
            showToast: true,
            toastType: "success",
            toastMessage: submitLaserLINKData?.data?.message,
            autohide: false,
          });
        } else {
          setSaveButtonStatus(true);
          setShowNotifications({
            showToast: true,
            toastType: "success",
            toastMessage: "LaserLINK saved successfully",
            delay: 2000,
            autohide: true,
          });
        }
      }
    } else if (
      Object.keys(errorList)?.filter((errKey) => errorList[errKey]?.length > 0)
        ?.length > 0 &&
      getValidationStatus == true
    ) {
      setShowNotifications({
        showToast: true,
        toastType: "alert",
        toastMessage: "Please review the errors",
      });
    }
  };

    const handleLaserLINKSCreateModal = async () => {
        const [domainListData, profileData, getUserData, landingPageHasJump] = await Promise.all([
            updateDomainListEvent(),
            updateProfileEvent(),
            networkService(`get-users.json`, "POST", {
                field: "current_user",
                value: true,
            }),
            checkLandingPageHasJumpToPage(docData?.id, defaultLaserLINKData.landing_page || 1)
        ]);

        const defaultDomain =
            domainListData?.find(d => d.default === true)
                ?.master_domain?.name || "";

        // Use the selected language from props, or fall back to the first available language
        const selectedLanguage = language ||
                                  docData?.doc_files_languages?.[0]?.language?.locale ||
                                  docData?.doc_files_languages?.[0]?.language ||
                                  "en-US";

        let valueToUpdate = {
            ...defaultLaserLINKData,
            doc_files_languages: docData.doc_files_languages,
            doc_id: docData?.id,
            language: selectedLanguage,  // Use selected language instead of default
            languages: selectedLanguage, // Also set languages field for consistency

            domain: defaultDomain,

            notification_emails: getUserData.data,
            thumbnail: docData?.thumb || thumbnail,
            thumb: docData?.thumb,
            expire_time_zone: profileData?.time_zone ?? "",
            expire_time: docData?.expire_time ?? "0:0",
            opt_in: { ...defaultPopupOptions },
        };

        // Auto-enable CYOA if landing page has Jump to Page elements
        if (landingPageHasJump) {
            valueToUpdate = {
                ...valueToUpdate,
                cyoa_enabled: true,
                disable_scroll: true,
                disable_settings: {
                    ...valueToUpdate.disable_settings,
                    disable_on: 'cyoa'
                }
            };
        }

        setLanguageConfirm(null);
        setEditLaserLinkItem(valueToUpdate);
        updateLaserLinkData(docData?.id, "active");
        updateUserLightboxesDoc(docData);
        updateUserLightboxesPage(docData);
        updateUserExitPopData(docData);
    };

  const triggerCopyAnimation = () => {
    setIsCopyAnimating(true);
    if (copyAnimationTimeoutRef.current) {
      clearTimeout(copyAnimationTimeoutRef.current);
    }
    copyAnimationTimeoutRef.current = setTimeout(() => {
      setIsCopyAnimating(false);
      copyAnimationTimeoutRef.current = null;
    }, 350);
  };

  const handleClipboardCopy = (text, ref) => {
    if (!text) return;

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    if (ref?.current) {
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(ref.current);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    triggerCopyAnimation();
  };

  useEffect(() => {
    return () => {
      if (copyAnimationTimeoutRef.current) {
        clearTimeout(copyAnimationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isSubmitting && Object.keys(errorList).length > 0) {
      handleSubmitLaserLINKDataEvent();
      setIsSubmitting(false); // Reset submission state after execution
    }
  }, [errorList]);

  useEffect(() => {
    // Don't open modal if we just closed it (prevents reopening after Copy & Close)
    if (justClosedRef.current) {
      if (laserItem != null) {
        // Don't reset justClosedRef here - let it reset after triggerUpdateList completes
        return;
      }
      return;
    }
    
    if (laserItem == null) {
      return;
    }
    
    setSaveButtonStatus(null);
    if (laserItem != null && laserItem !== true) {
      handleLaserLINKSettingsModal(laserItem);
      // initialLaserLinkItem.current will be set in handleLaserLINKSettingsModal after data is complete
    } else if (laserItem === true) {
      handleLaserLINKSCreateModal();
      initialLaserLinkItem.current = null; // new record
    }
  }, [laserItem]);

  useEffect(() => {
    // Don't update selectedKDFLink if we just closed the modal (prevents reopening)
    if (justClosedRef.current) {
      return;
    }
    
    if (editLaserLinkItem == null) {
      updateSelectedKDFLink(null);
    }
  }, [editLaserLinkItem]);

  // Sync initialLaserLinkItem.current after initialization to account for any child component updates
  // This ensures isDirty tracking works correctly after modal opens
  useEffect(() => {
    if (isInitializingRef.current && editLaserLinkItem && initialLaserLinkItem.current) {
      const timeoutId = setTimeout(() => {
        if (isInitializingRef.current) {
          // Sync the initial state to match current state (in case child components updated it)
          initialLaserLinkItem.current = JSON.parse(JSON.stringify(editLaserLinkItem));
          isInitializingRef.current = false;
          setIsDirty(false);
        }
      }, 150);
      
      return () => clearTimeout(timeoutId);
    }
  }, [editLaserLinkItem]);

  // Derived: true whenever ANY tab's error list is non-empty (e.g. landing_page_gt_optin).
  // Used to disable the Save button without relying on the deferred validateInnerData path.
  const hasValidationErrors = Object.values(errorList).some(arr => arr.length > 0);

  return (
    <>
      <CommonModal
        modalStatus={languageConfirm != null}
        changeActiveStatus={() => setLanguageConfirm(null)}
        title="Change Language?"
        centered
        classList={"upload-kdf-modal-wrapper alert-modal"}
        footer={
          <div className="d-flex m-auto">
            <Button
              text={"No"}
              classList={"alert delete-btn"}
              addonClass={"my-2 mx-2"}
              onClick={() => setLanguageConfirm(null)}
            />
            <Button
              text={"Yes"}
              classList={"success delete-btn"}
              addonClass={"my-2 mx-2 common-modal-yes-button"}
              onClick={() => {
                if (languageConfirm) {
                  updateLanguageSelection(languageConfirm);
                }
                setLanguageConfirm(null);
              }}
            />
          </div>
        }
      >
        <p style={{ fontSize: "14px" }}>
          If you switch languages, all previous recipients of this link will initially
          see this KDF in the new language (not the originally sent) Do you want to
          proceed?
        </p>
      </CommonModal>

      <CommonModal
        modalStatus={embedCode != null}
        title={"Embed"}
        footer={
          <Button
            text={"Copy embed code </>"}
            classList={"success laser-link-btn"}
            addonClass={"my-2 mx-1"}
            onClick={async () => {
              await navigator.clipboard.writeText(embedCode ?? "");
              setEmbedCode(null);
            }}
          />
        }
        changeActiveStatus={(status) => !status && setEmbedCode(null)}
        classList={"embed-modal"}
        centered
      >
        <textarea
          value={embedCode}
          onChange={(em) => setEmbedCode((prev) => (prev += em.target.value))}
          style={{ width: "100%" }}
          rows={10}
        />
      </CommonModal>

      <CommonModal
        className={"laserLinkModal"}
        title={
          batchStatus == true ? (
            <strong>MultiLink Generation:</strong>
          ) : (
            <>
            <Input
              value={editLaserLinkItem?.name ?? ""}
              placeholder="Insert LaserLINK title here"
              name="name"
              forRef={titleInputRef}
              onChange={(e) => {
                handleOnchangeEvent(e);
                setSaveButtonStatus(false);
              }}
              required={true}
              autoFocus={true}
              errorMessageStatus={errorList?.settings?.includes("name")}
            />
            {/* {errorList?.settings?.includes("name") && (
            <small className="text-danger d-block fs-6 mt-0 mb-0">
              Title is required.
            </small>
          )} */}
            </>
          )
        }
        changeActiveStatus={(status) => !status && closeModal()}
        modalStatus={editLaserLinkItem != null}
        centered
        footer={
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div className="d-flex align-items-center">
              {editLaserLinkItem?.tracking_link && (
                <>
                  <a
                    href={editLaserLinkItem?.tracking_link ?? "#"}
                    target="_blank"
                    ref={trackingLinkRef}
                    className="laser-link-text"
                  >
                    {editLaserLinkItem?.tracking_link ?? ""}
                  </a>
                  <CustomTooltip
                    title={
                      <p
                        className="p-1"
                        style={{ color: "black", padding: "4px" }}
                      >
                        Copy LaserLINK
                      </p>
                    }
                  >
                    <span>
                      {/* <FontAwesomeIcon
                        icon={faClipboard}
                        className="mx-2 cursor-pointer"
                        onClick={() =>
                          handleClipboardCopy(
                            editLaserLinkItem?.tracking_link,
                            trackingLinkRef
                          )
                        }
                      /> */}
                      <img
                        src="/icons/copy.svg"
                        className={`cursor-pointer ${styles.copyIcon} ${
                          isCopyAnimating ? styles.copyIconAnimated : ""
                        }`}
                        onClick={() =>
                          handleClipboardCopy(
                            editLaserLinkItem?.tracking_link,
                            trackingLinkRef
                          )
                        }
                      />
                    </span>
                  </CustomTooltip>
                </>
              )}
            </div>
            <div className="d-flex align-end">
              {editLaserLinkItem?.tracking_link && (
                <Button
                  text={"Embed"}
                  classList={"embed-button"}
                  addonClass={"my-2 mx-1"}
                  onClick={() =>
                    setEmbedCode(
                      createEmbedIframeCode(editLaserLinkItem?.tracking_link)
                    )
                  }
                />
              )}
              {/* <Button
                                disabled={(saveButtonStatus == null || !editLaserLinkItem?.name || isError)}
                                text={`${saveButtonStatus == true ? 'Copy LaserLINK and close' : (batchStatus != true ? 'Save changes' : 'Save and Close')}`}
                                classList={`success laser-link-btn ${(saveButtonStatus == null || !editLaserLinkItem?.name || isError) ? 'disabled' : ''}`}
                                addonClass={'my-2 mx-1'}
                                onClick={handleSaveLaserLINKData}
                            /> */}
              <Button
                icon={
                  !batchStatus &&
                  saveButtonStatus === true &&
                  editLaserLinkItem?.tracking_link &&
                  editLaserLinkItem?.name ? (
                    <img src="/icons/copy.svg" alt="" className={styles.copyIcon} />
                  ) : null
                }
                text={`${
                  batchStatus
                    ? "Save and Close"
                    : saveButtonStatus === true &&
                      editLaserLinkItem?.tracking_link &&
                      editLaserLinkItem?.name
                    ? "Copy LaserLINK & Close"
                    : "Save changes"
                }`}
                classList={`success laser-link-btn ${
                  (batchStatus
                    ? !editLaserLinkItem?.batch_link_array?.length
                    : saveButtonStatus === true &&
                      editLaserLinkItem?.tracking_link &&
                      editLaserLinkItem?.name
                    ? !editLaserLinkItem?.name
                    : !editLaserLinkItem?.name || !isDirty) || isError || hasValidationErrors
                    ? "disabled"
                    : ""
                }`}
                addonClass={`my-2 mx-1 ${
                  saveButtonStatus === true &&
                  editLaserLinkItem?.tracking_link &&
                  editLaserLinkItem?.name
                    ? styles.wideCopyButton
                    : ""
                }`}
                disabled={
                  (batchStatus
                    ? !editLaserLinkItem?.batch_link_array?.length
                    : saveButtonStatus === true &&
                      editLaserLinkItem?.tracking_link &&
                      editLaserLinkItem?.name
                    ? !editLaserLinkItem?.name
                    : !editLaserLinkItem?.name || !isDirty) || isError || hasValidationErrors
                }
                onClick={handleSaveLaserLINKData}
              />
              {/* {JSON.stringify(isDirty)}
                            <Button
  disabled={
    (batchStatus
      ? !editLaserLinkItem?.batch_link_array?.length
      : !editLaserLinkItem?.name || !isDirty) || isError
  }
  text={`${
    batchStatus
      ? "Save and Close"
      : editLaserLinkItem?.name && editLaserLinkItem?.tracking_link
      ? "Copy LaserLINK and Close"
      : "Save changes"
  }`}
  classList={`success laser-link-btn ${
    (batchStatus
      ? !editLaserLinkItem?.batch_link_array?.length
      : !editLaserLinkItem?.name || !isDirty) || isError
      ? "disabled"
      : ""
  }`}
  addonClass="my-2 mx-1"
  onClick={handleSaveLaserLINKData}
/> */}
            </div>
          </div>
        }
      >
        <div ref={modalRootRef}>
        <Tabs defaultActiveKey="settings" className={`linkTabs`}>
          <Tab
            eventKey="settings"
            className={`nnnnn`}
            title={
              <>
                LaserLINK Settings{" "}
                {errorList?.settings?.length > 0 ? (
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="alert-icon"
                  />
                ) : (
                  ""
                )}
              </>
            }
          >
            <LaserLINKSettingsTab
              editLaserLinkItem={editLaserLinkItem}
              handleOnchangeEvent={handleOnchangeEvent}
              updateEditLaserLinkData={(value) => {
                setEditLaserLinkItem(value);
                setSaveButtonStatus(false);
              }}
              getDomains={getDomains}
              getDocFiles={getDocFiles}
              validationTrigger={getValidationStatus}
              errorListData={errorList?.settings}
              updateErrorStatus={(status) =>
                setErrorList((prevState) => ({
                  ...prevState,
                  settings: status,
                }))
              }
              // updateValidationTab={(value) => setValiationTabMessage(prev => ({...prev, 'settings': value}))}
              batchStatus={batchStatus}
              setSaveButtonStatus={setSaveButtonStatus}
              batchFile={batchFile}
              setBatchFile={setBatchFile}
              setIsError={setIsError}
            />
          </Tab>
          <Tab eventKey="notify" title="Send Notification">
            <SendNotificationTab
              editLaserLinkItem={editLaserLinkItem}
              updateEditLaserLinkData={(value) => {
                setEditLaserLinkItem(value);
                setSaveButtonStatus(false);
              }}
              getDocFiles={getDocFiles}
              errorListData={errorList?.settings}
              setIsError={setIsError}
            />
          </Tab>
          <Tab eventKey="access" title="Access Gate Options">
            <AccessGateOptionsTab
              editLaserLinkItem={editLaserLinkItem}
              updateEditLaserLinkItem={(value) => {
                setEditLaserLinkItem(value);
                setSaveButtonStatus(false);
              }}
              time_zone_list={time_zone_list}
              getDocFiles={getDocFiles}
              defaultLinkTexts={getDefault_link_texts}
              updateDefaultLink={setDefault_link_texts}
              validationTrigger={getValidationStatus}
              errorListData={errorList?.access}
              updateErrorStatus={(status) =>
                setErrorList((prevState) => ({ ...prevState, access: status }))
              }
              getProfile={getProfile}
              // updateValidationTab={(value) => setValiationTabMessage(prev => ({...prev, 'access': value}))}
              batchStatus={batchStatus}
            />
          </Tab>
          <Tab eventKey="popup" title="Popup Options">
            <PopupOptionsTab
              editLaserLinkItem={editLaserLinkItem}
              updateEditLaserLinkItem={(value) => {
                setEditLaserLinkItem(value);
                setSaveButtonStatus(false);
              }}
              popupOptions={popupOptions}
              validationTrigger={getValidationStatus}
              errorListData={errorList?.popup}
              updateErrorStatus={(status) =>
                setErrorList((prevState) => ({ ...prevState, popup: status }))
              }
              getDocFiles={getDocFiles}
              // updateValidationTab={(value) => setValiationTabMessage(prev => ({...prev, 'popup': value}))}
              batchStatus={batchStatus}
            />
          </Tab>
        </Tabs>
        </div>
      </CommonModal>
    </>
  );
};

export default LaserLinkModal;
